import { useState, useEffect, useCallback, SyntheticEvent } from 'react';
import moment from 'moment';
import { SelectProps, SelectChangeEvent } from '@mui/material';
import { MomentChangeHandler } from '@core/interfaces/moment-change-handler';
import { IProps as IAutocompleteProps } from '@ui/components/Autocomplete/IProps';
import { useStore } from '@ui/hooks/useStore';
import { IState } from './IState';

const useSupport = () => {
    const { map, lang, user, groups, app, devices, telemetry } = useStore();
    const fromDateFilterCurrent = moment(map.fromDateFilter);
    const toDateFilterCurrent = moment(map.toDateFilter);

    const initState: IState = {
        firstLoading: true,
        fromDateFilterCurrent,
        toDateFilterCurrent,
    };
    const [state, setState] = useState(initState);

    const handleFromDateChange: MomentChangeHandler = useCallback(
        (date) => {
            setState((prev) => ({ ...prev, fromDateFilterCurrent: date }));
            if (date.isValid()) {
                const tz = user.settings.timezone;
                map.setFromDateFilter(
                    date.utc(true).subtract(tz, 'm').toDate(),
                );
            }
        },
        [user.settings.timezone],
    );
    const handleToDateChange: MomentChangeHandler = useCallback(
        (date) => {
            setState((prev) => ({ ...prev, toDateFilterCurrent: date }));
            if (date.isValid()) {
                const tz = user.settings.timezone;
                map.setToDateFilter(date.utc(true).subtract(tz, 'm').toDate());
            }
        },
        [user.settings.timezone],
    );
    const handleDateFilterChange: SelectProps<string>['onChange'] = useCallback(
        (e: SelectChangeEvent<string>) => {
            const dateFilter = e.target.value;
            map.setDateFilter(dateFilter);
        },
        [],
    );
    const handleDeviceChange: IAutocompleteProps['onChange'] = useCallback(
        (e: SyntheticEvent<Element, Event>, imei: string) => {
            if (
                imei &&
                devices.supportList.includes(imei) &&
                map.selectedDevice !== imei
            ) {
                map.setSelectedDevice(imei);
            }
        },
        [devices.supportList, map.selectedDevice],
    );
    const handleSwitchTrackMode = useCallback(() => {
        map.switchTrackMode();
    }, []);
    const handleUpdateClick = useCallback(() => {
        map.updateHistoryCoordsList();
        telemetry.setTelemetryVisible();

        if (!map.selectedDevice) return;
        const [device] = map.lastCoordsList.filter((device) => {
            return device.imei === map.selectedDevice;
        });

        if (device) {
            const { lat, lon } = device.gpsCoord;
            if (lat && lon) {
                map.setCurrentPosition([lat, lon]);
            }
            map.setPosition(device.gpsCoord);
            map.setZoom(18);
        }
    }, []);
    const handleSettingsOpen = useCallback(() => {
        const imei = map.selectedDevice;
        if (imei) {
            devices.setSettingDeviceImei(imei);
            app.openDeviceSettings();
        }
    }, []);
    const handlers = {
        handleFromDateChange,
        handleToDateChange,
        handleDateFilterChange,
        handleDeviceChange,
        handleSwitchTrackMode,
        handleUpdateClick,
        handleSettingsOpen,
    };

    useEffect(() => {
        return () => {
            const selectedGroupId = localStorage.getItem('groupId');
            selectedGroupId && map.setSelectedGroupId(selectedGroupId);
        };
    }, []);
    useEffect(() => {
        const imei = map.selectedDevice;
        imei && devices.getDeviceLastCoords(imei);
    }, [map.selectedDevice]);
    useEffect(() => {
        const imei = map.selectedDevice;
        if (app.deviceSettingsVisible && imei) {
            app.closeDeviceSettings();
            setTimeout(() => {
                devices.setSettingDeviceImei(imei);
                app.openDeviceSettings();
            }, 0);
        }
    }, [map.selectedDevice]);
    useEffect(() => {
        setState((prev) => ({ ...prev, fromDateFilterCurrent }));
    }, [fromDateFilterCurrent.milliseconds()]);
    useEffect(() => {
        setState((prev) => ({ ...prev, toDateFilterCurrent }));
    }, [toDateFilterCurrent.milliseconds()]);
    useEffect(() => {
        const groupsLen = groups.groupsList.length;
        const devicesLen = devices.deviceList.length;
        if (
            (app.page === 'history' || app.page === 'support') &&
            state.firstLoading &&
            groupsLen > 0 &&
            devicesLen > 0 &&
            !map.selectedGroupId
        ) {
            let id = localStorage.getItem('groupId');
            if (!id) {
                [{ id }] = groups.groupsList.filter(
                    (group) => group.is_default,
                );
            }
            const listLen = devices.deviceList
                .filter((device) => device.groupId === id)
                .map(({ imei }) => imei).length;
            if (listLen > 0) {
                map.setSelectedGroupId(id);
                setState((prev) => ({ ...prev, firstLoading: false }));
            }
        }
    }, [groups.groupsList, devices.deviceList, app.page]);

    return {
        map,
        lang,
        devices,
        user,
        state,
        handlers,
    };
};

export default useSupport;
